import { useEffect, useState } from 'react'
import { Laptop } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useI18n } from '@/lib/i18n'
import Header from '@/components/layout/Header'
import Footer from '@/components/layout/Footer'
import MazeArt from '@/components/ui/MazeArt'
import {
  createSession,
  writeSession,
  type SandboxLang,
  type SandboxScenario,
  type SandboxSession,
  type SandboxTheme,
} from '@/lib/sandbox'
import type { SandboxProject } from '@/lib/sandboxProjects'

type RoleCopy = { name: string; who: string; desc: string }
type ProjectCopy = { name: string; title: string; lede: string; roles: Record<string, RoleCopy>; rolesNote?: string }
type Copy = {
  eyebrow: string
  start: string
  howTitle: string
  how: string[]
  rolesTitle: string
  setupTitle: string
  language: string
  theme: string
  light: string
  dark: string
  scenario: string
  scenarioBusy: string
  scenarioBusyHint: string
  scenarioEmpty: string
  scenarioEmptyHint: string
  privacy: string
  desktopOnlyTitle: string
  desktopOnly: string
  copyLink: string
  linkCopied: string
  disclaimerTitle: string
  disclaimer: string
}

type Props = { project: SandboxProject; onStarted: (s: SandboxSession) => void }

/** Below tablet width the apps are unusable inside the sandbox frame. */
const PHONE_QUERY = '(max-width: 767px)'

function useIsPhone() {
  // false on the first render so it matches the prerendered HTML
  const [phone, setPhone] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia?.(PHONE_QUERY)
    if (!mq) return
    const on = () => setPhone(mq.matches)
    on()
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  return phone
}

export default function SandboxStart({ project, onStarted }: Props) {
  const { section, t, to, lang: siteLang } = useI18n()
  const c = section('sandbox') as unknown as Copy
  const pc = section(`sandboxProjects.${project.id}`) as unknown as ProjectCopy

  const [role, setRole] = useState<string>(project.defaultRole)
  const [appLang, setAppLang] = useState<SandboxLang>(siteLang === 'EN' ? 'en' : 'de')
  const [theme, setTheme] = useState<SandboxTheme>('light')
  const [scenario, setScenario] = useState<SandboxScenario>('busy')
  const isPhone = useIsPhone()
  const [copied, setCopied] = useState(false)

  const start = () => {
    const s = createSession(project, {
      role,
      lang: appLang,
      theme,
      scenario,
    })
    writeSession(project, s)
    onStarted(s)
  }

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      setCopied(true)
    } catch {
      /* clipboard blocked — the URL is still in the address bar */
    }
  }

  // On a phone the sandbox cannot be used: ask for a desktop instead of
  // starting a session the visitor cannot see.
  const StartButton = () =>
    isPhone ? (
      <div className="sandbox-start__desktop-only" role="note">
        <Laptop size={22} strokeWidth={1.75} aria-hidden />
        <div>
          <p className="sandbox-start__desktop-title">{c.desktopOnlyTitle}</p>
          <p>{c.desktopOnly}</p>
          <button type="button" className="btn btn--sm" onClick={copyLink}>
            {copied ? c.linkCopied : c.copyLink}
          </button>
        </div>
      </div>
    ) : (
      <button type="button" className="btn btn--primary" onClick={start}>
        {c.start}
        <span className="arrow" aria-hidden>
          →
        </span>
      </button>
    )

  return (
    <>
      <Header active="projects" onNavigate={() => {}} />
      <main className="container sandbox-start">
        <nav className="sandbox-start__crumbs mono" aria-label="Breadcrumb">
          <Link to={to('/projects')}>{t('nav.projects')}</Link>
          <span aria-hidden> / </span>
          <span>{pc.name}</span>
        </nav>

        <div className="sandbox-start__hero">
          <div>
            <span className="eyebrow">{c.eyebrow}</span>
            <h1 style={{ marginTop: 14 }}>{pc.title}</h1>
            <p className="lede" style={{ marginTop: 22 }}>
              {pc.lede}
            </p>
            <div className="sandbox-start__actions">
              <StartButton />
              <span className="mono muted sandbox-start__privacy">{c.privacy}</span>
            </div>
            <p className="sandbox-start__disclaimer" role="note">
              <strong>{c.disclaimerTitle}.</strong> {c.disclaimer}
            </p>
          </div>
          <figure className="sandbox-start__art" aria-hidden>
            <MazeArt cols={18} rows={18} seed={2026} />
          </figure>
        </div>

        <section className="sandbox-start__block" aria-labelledby="sb-how">
          <div className="split">
            <div className="split__aside">
              <span className="eyebrow">01</span>
              <h2 id="sb-how" style={{ marginTop: 12 }}>
                {c.howTitle}
              </h2>
            </div>
            <ol className="sandbox-start__how">
              {(Array.isArray(c.how) ? c.how : []).map((step, i) => (
                <li key={i}>
                  <span className="mono muted">0{i + 1}</span>
                  <p>{step}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="sandbox-start__block" aria-labelledby="sb-roles">
          <div className="split">
            <div className="split__aside">
              <span className="eyebrow">02</span>
              <h2 id="sb-roles" style={{ marginTop: 12 }}>
                {c.rolesTitle}
              </h2>
            </div>
            <div>
            <div className="sandbox-start__roles" role="radiogroup" aria-labelledby="sb-roles">
              {project.roles.map((r) => {
                const rc = pc.roles?.[r.key]
                const checked = role === r.key
                return (
                  <button
                    key={r.key}
                    type="button"
                    role="radio"
                    aria-checked={checked}
                    className={`role-card ${checked ? 'is-checked' : ''}`}
                    onClick={() => setRole(r.key)}
                  >
                    <span className="role-card__top">
                      <span className="role-card__name">{rc?.name ?? r.key}</span>
                      <span className="role-card__check" aria-hidden />
                    </span>
                    <span className="role-card__who mono">{rc?.who}</span>
                    <span className="role-card__desc">{rc?.desc}</span>
                  </button>
                )
              })}
            </div>
            {pc.rolesNote && <p className="sandbox-start__roles-note">{pc.rolesNote}</p>}
            </div>
          </div>
        </section>

        <section className="sandbox-start__block" aria-labelledby="sb-setup">
          <div className="split">
            <div className="split__aside">
              <span className="eyebrow">03</span>
              <h2 id="sb-setup" style={{ marginTop: 12 }}>
                {c.setupTitle}
              </h2>
            </div>
            <div className="sandbox-setup">
              <div className="sandbox-setup__row">
                <div className="field">
                  <span className="field__label">{c.language}</span>
                  <div className="seg" role="radiogroup" aria-label={c.language}>
                    {(['de', 'en'] as const).map((l) => (
                      <button key={l} type="button" role="radio" aria-checked={appLang === l} onClick={() => setAppLang(l)}>
                        {l === 'de' ? 'Deutsch' : 'English'}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="field">
                  <span className="field__label">{c.theme}</span>
                  <div className="seg" role="radiogroup" aria-label={c.theme}>
                    {(['light', 'dark'] as const).map((t) => (
                      <button key={t} type="button" role="radio" aria-checked={theme === t} onClick={() => setTheme(t)}>
                        {t === 'light' ? c.light : c.dark}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="field">
                <span className="field__label">{c.scenario}</span>
                <div className="sandbox-setup__scenarios" role="radiogroup" aria-label={c.scenario}>
                  {(
                    [
                      ['busy', c.scenarioBusy, c.scenarioBusyHint],
                      ['empty', c.scenarioEmpty, c.scenarioEmptyHint],
                    ] as const
                  ).map(([key, label, hint]) => (
                    <button
                      key={key}
                      type="button"
                      role="radio"
                      aria-checked={scenario === key}
                      className={`scenario ${scenario === key ? 'is-checked' : ''}`}
                      onClick={() => setScenario(key)}
                    >
                      <span className="scenario__label">{label}</span>
                      <span className="scenario__hint">{hint}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="sandbox-setup__go">
                <StartButton />
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  )
}
